import React, { useEffect, useState } from 'react';
import { Box, Switch, Button, CircularProgress, Tabs, Tab } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { wagerConfigService } from 'components/api/wagerConfig';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import { formatDate } from 'utils/date';
import { Alert } from 'components/alert/alert';
import styles from './WagerConfig.module.scss';

interface WagerConfigProps {
  siteId: string | number;
}

const WagerConfig: React.FC<WagerConfigProps> = ({ siteId }) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSub, setLoadingSub] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMultiplier, setEditMultiplier] = useState<string>('');
  const [statusAlert, setStatusAlert] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await wagerConfigService.getWagerCategories(siteId);
      setCategories(response.data?.data?.categories || response.data?.data || []);
    } catch (err: any) {
      setStatusAlert({
        message: err.response?.data?.message || err.message || 'Failed to load wager categories',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [siteId]);

  useEffect(() => {
    if (categories.length > 0) {
      const categoryId = categories[tabValue]?.id;
      if (categoryId) {
        fetchSubCategories(categoryId);
      }
    }
  }, [categories, tabValue, siteId]);

  const fetchSubCategories = async (categoryId: string | number) => {
    setLoadingSub(true);
    try {
      const response = await wagerConfigService.getWagerSubCategories(siteId, categoryId);
      const dataField = response.data?.data;
      setSubCategories(dataField?.subCategories || (Array.isArray(dataField) ? dataField : []));
    } catch (err: any) {
      setStatusAlert({
        message: err.response?.data?.message || err.message || 'Failed to load subcategories',
        type: 'error',
      });
    } finally {
      setLoadingSub(false);
    }
  };

  const handleToggleStatus = async (id: number, isActive: boolean) => {
    try {
      // optimistic update
      setSubCategories((prev) =>
        prev.map((item) => (item.id === id ? { ...item, isActive } : item)),
      );
      await wagerConfigService.updateWagerCategory(id, { isActive });
      setStatusAlert({ message: 'Status updated successfully', type: 'success' });
    } catch (err: any) {
      // revert on error
      setSubCategories((prev) =>
        prev.map((item) => (item.id === id ? { ...item, isActive: !isActive } : item)),
      );
      setStatusAlert({
        message: err.response?.data?.message || err.message || 'Failed to update status',
        type: 'error',
      });
    }
  };

  const handleSaveMultiplier = async (id: number) => {
    try {
      await wagerConfigService.updateWagerCategory(id, { multiplier: editMultiplier });
      setSubCategories((prev) =>
        prev.map((item) => (item.id === id ? { ...item, multiplier: editMultiplier } : item)),
      );
      setEditingId(null);
      setStatusAlert({ message: 'Multiplier updated successfully', type: 'success' });
    } catch (err: any) {
      setStatusAlert({
        message: err.response?.data?.message || err.message || 'Failed to update multiplier',
        type: 'error',
      });
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'multiplier',
      headerName: 'Multiplier',
      flex: 1,
      renderCell: (params) => {
        const isEditing = editingId === params.row.id;
        if (isEditing) {
          return (
            <input
              type="text"
              className={styles.cellInput}
              value={editMultiplier}
              onChange={(e) => setEditMultiplier(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          );
        }
        return <span>{params.value}</span>;
      },
    },
    {
      field: 'isActive',
      headerName: 'Is Active',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={(e) => handleToggleStatus(params.row.id, e.target.checked)}
          size="small"
        />
      ),
    },
    { field: 'gameCount', headerName: 'Game Count', width: 120 },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      flex: 1,
      renderCell: (params) => <span>{formatDate(params.value)}</span>,
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 150,
      renderCell: (params) => {
        const isEditing = editingId === params.row.id;
        const hasChanged = editMultiplier !== params.row.multiplier;

        if (isEditing) {
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                className={styles.actionBtn}
                onClick={() => {
                  setEditingId(null);
                  setEditMultiplier('');
                }}
                sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={styles.actionBtn}
                onClick={() => handleSaveMultiplier(params.row.id)}
                disabled={!hasChanged}
                sx={{
                  backgroundColor: 'var(--accent-blue)',
                  '&:hover': { backgroundColor: '#0092c7' },
                }}
              >
                Save
              </Button>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              className={styles.actionBtn}
              onClick={() => {
                setEditingId(params.row.id);
                setEditMultiplier(params.row.multiplier);
              }}
              sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
            >
              Edit
            </Button>
          </Box>
        );
      },
    },
  ];

  if (loading && categories.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} sx={{ color: '#00b3f5' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {statusAlert && <Alert message={statusAlert.message} type={statusAlert.type} />}

      {categories.length > 0 && (
        <Box className={styles.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            className={styles.categoryTabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            {categories.map((category, index) => (
              <Tab key={category.id} label={category.name} value={index} />
            ))}
          </Tabs>
        </Box>
      )}

      <Box className={commonStyles.gridWrapper}>
        <DataGrid
          rows={subCategories}
          rowCount={subCategories.length}
          getRowId={(row: any) => row.id}
          columns={columns}
          loading={loadingSub}
          sx={commonGridStyles}
          hideFooter
        />
      </Box>
    </Box>
  );
};

export default WagerConfig;
